import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@tutlo/ui'
import { useState } from 'react'
import { useAdmin, useKnowledge, useRevertKnowledge, useSaveKnowledge } from '../admin.ts'

export const Route = createFileRoute('/admin_/wiedza')({
  component: KnowledgeRoute,
})

const MAX_CHARS = 20_000

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('pl-PL', { dateStyle: 'medium', timeStyle: 'short' })

/**
 * M6: panel edit of the agent's knowledge base (single markdown document, ARCHITECTURE §9).
 * Saves append a version; the newest version is live for the chat agent within ~a minute.
 */
function KnowledgeRoute() {
  const admin = useAdmin()
  const knowledge = useKnowledge()
  const save = useSaveKnowledge()
  const revert = useRevertKnowledge()
  // `null` = no local edits; the textarea then mirrors the server copy, so a save/revert
  // from the server side flows straight into the editor without effect juggling.
  const [draft, setDraft] = useState<string | null>(null)
  const [tab, setTab] = useState<'edit' | 'preview'>('edit')
  const serverContent = knowledge.data?.content ?? null

  if (admin.isPending) {
    return <Center>Ładowanie…</Center>
  }
  if (!admin.data) {
    return (
      <Center>
        <h1 className="text-h2">Panel analityki Tutlo</h1>
        <p className="mt-md text-body text-text-secondary">
          Dostęp przez link z kluczem. Poproś zespół CYB o link do panelu.
        </p>
      </Center>
    )
  }

  const value = draft ?? serverContent ?? ''
  const dirty = draft !== null && draft !== serverContent
  const tooLong = value.length > MAX_CHARS

  const onSave = () => {
    if (!dirty || tooLong) return
    save.mutate({ content: value }, { onSuccess: () => setDraft(null) })
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-[960px] flex-col gap-md px-base py-base">
      <header className="flex flex-wrap items-center gap-md">
        <h1 className="font-bold text-[15px]">Wiedza agenta</h1>
        <Link to="/admin" className="text-label-strong text-selected underline underline-offset-2">
          ← Wróć do analityki
        </Link>
        <span className="ml-auto text-label text-text-secondary">
          {knowledge.data?.updatedAt
            ? `Ostatnia zmiana: ${formatDate(knowledge.data.updatedAt)}`
            : 'Wersja wbudowana (jeszcze nie edytowana)'}
        </span>
      </header>

      <p className="text-label text-text-secondary">
        Ten dokument to fakty, którymi bot odpowiada w czacie. Zachowanie bota (jedno pytanie na
        turę, zakaz cen, narzędzia) jest ustawione osobno w kodzie i tu go nie zmienisz. Zapis
        działa w rozmowach w ciągu minuty.
      </p>

      {knowledge.isPending ? (
        <p className="text-body text-text-secondary">Ładowanie…</p>
      ) : (
        <>
          <div
            className="flex gap-[4px] self-start rounded-pill bg-bg-card p-[4px] shadow-soft"
            role="tablist"
          >
            {(['edit', 'preview'] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                onClick={() => setTab(t)}
                className={`rounded-pill px-md py-sm text-label-strong ${
                  tab === t ? 'bg-selected text-white' : 'text-text-secondary'
                }`}
              >
                {t === 'edit' ? 'Edycja' : 'Podgląd'}
              </button>
            ))}
          </div>
          {tab === 'edit' ? (
            <textarea
              value={value}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
              className="min-h-[480px] w-full resize-y rounded-card border border-border-soft bg-bg-card p-base font-mono text-[13px] leading-relaxed focus-visible:outline-2 focus-visible:outline-selected"
              aria-label="Treść bazy wiedzy (markdown)"
            />
          ) : (
            <div className="min-h-[480px] rounded-card border border-border-soft bg-bg-card p-lg">
              <MarkdownPreview source={value} />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-md">
            <Button onClick={onSave} disabled={!dirty || tooLong || save.isPending}>
              {save.isPending ? 'Zapisywanie…' : 'Zapisz nową wersję'}
            </Button>
            <span className={`text-label ${tooLong ? 'text-error' : 'text-text-secondary'}`}>
              {value.length.toLocaleString('pl-PL')} / {MAX_CHARS.toLocaleString('pl-PL')} znaków
            </span>
            {save.isError && (
              <span className="text-label text-error">
                Nie udało się zapisać — spróbuj ponownie.
              </span>
            )}
            {dirty && !save.isPending && (
              <button
                type="button"
                onClick={() => setDraft(null)}
                className="text-label text-text-secondary underline underline-offset-2"
              >
                Odrzuć zmiany
              </button>
            )}
          </div>

          {(knowledge.data?.versions.length ?? 0) > 0 && (
            <section className="mt-md">
              <h2 className="mb-sm text-label-strong uppercase tracking-[0.06em] text-text-secondary">
                Historia wersji
              </h2>
              <ul className="flex flex-col">
                {knowledge.data?.versions.map((v, i) => (
                  <li
                    key={v.id}
                    className="flex items-center gap-md border-border-soft border-t py-sm text-label"
                  >
                    <span>{formatDate(v.createdAt)}</span>
                    <span className="text-text-secondary">
                      {v.chars.toLocaleString('pl-PL')} znaków
                    </span>
                    {i === 0 ? (
                      <span className="ml-auto text-success-text">aktualna</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => revert.mutate({ id: v.id })}
                        disabled={revert.isPending}
                        className="ml-auto text-selected underline underline-offset-2"
                      >
                        przywróć
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </main>
  )
}

/** Inline markers only: **bold** and `code`. Returned as React nodes — no HTML injection. */
function inline(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = []
  const re = /\*\*([^*]+)\*\*|`([^`]+)`/g
  let last = 0
  let m = re.exec(text)
  let key = 0
  while (m !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    if (m[1] !== undefined) {
      out.push(<strong key={key++}>{m[1]}</strong>)
    } else {
      out.push(
        <code key={key++} className="rounded-[4px] bg-bg px-[4px] font-mono text-[0.9em]">
          {m[2]}
        </code>,
      )
    }
    last = m.index + m[0].length
    m = re.exec(text)
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/**
 * Hand-rolled preview for the subset the knowledge doc uses (##/### headings, "-" lists,
 * numbered steps, bold, inline code). Deliberately no markdown dependency (bundle budget,
 * ARCHITECTURE §0) — this is a reading aid, not a faithful CommonMark renderer.
 */
function MarkdownPreview({ source }: { source: string }) {
  const blocks: React.ReactNode[] = []
  let list: { ordered: boolean; items: string[] } | null = null
  let key = 0

  const flushList = () => {
    if (!list) return
    const Tag = list.ordered ? 'ol' : 'ul'
    const items: React.ReactNode[] = []
    for (const item of list.items) {
      items.push(<li key={key++}>{inline(item)}</li>)
    }
    blocks.push(
      <Tag
        key={key++}
        className={`flex flex-col gap-[6px] pl-lg text-body ${list.ordered ? 'list-decimal' : 'list-disc'}`}
      >
        {items}
      </Tag>,
    )
    list = null
  }

  for (const raw of source.split('\n')) {
    const line = raw.trimEnd()
    const bullet = /^[-*]\s+(.*)$/.exec(line)
    const numbered = /^\d+\.\s+(.*)$/.exec(line)
    if (bullet?.[1] !== undefined || numbered?.[1] !== undefined) {
      const ordered = numbered !== null
      const item = (ordered ? numbered?.[1] : bullet?.[1]) ?? ''
      if (list && list.ordered !== ordered) flushList()
      list ??= { ordered, items: [] }
      list.items.push(item)
      continue
    }
    flushList()
    if (line === '') continue
    const heading = /^(#{1,4})\s+(.*)$/.exec(line)
    if (heading?.[2] !== undefined) {
      const level = heading[1]?.length ?? 2
      blocks.push(
        level <= 2 ? (
          <h2 key={key++} className="mt-lg text-h3 first:mt-0">
            {inline(heading[2])}
          </h2>
        ) : (
          <h3 key={key++} className="mt-md text-body-strong first:mt-0">
            {inline(heading[2])}
          </h3>
        ),
      )
      continue
    }
    blocks.push(
      <p key={key++} className="text-body">
        {inline(line)}
      </p>,
    )
  }
  flushList()
  return <div className="flex flex-col gap-md">{blocks}</div>
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-[480px] flex-col justify-center px-base text-body">
      {children}
    </main>
  )
}
